import React from "react";
import { Divider, Row, Typography } from "antd";
import type { ReactNode } from "react";
import { PageLayoutContext, type PageLayoutHeaderConfig } from "./PageLayout";

const { Title, Paragraph } = Typography;
const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? React.useLayoutEffect : React.useEffect;

type DashboardViewContentProps = {
  title: ReactNode;
  secondaryTitle?: ReactNode;
  actions?: ReactNode;
  children: React.ReactNode;
};

const DashboardViewContent = ({
  title,
  secondaryTitle,
  actions,
  children,
}: DashboardViewContentProps) => {
  const pageLayoutContext = React.useContext(PageLayoutContext);
  const setPageLayoutHeader = pageLayoutContext?.setHeader;
  const clearPageLayoutHeader = pageLayoutContext?.clearHeader;

  useIsomorphicLayoutEffect(() => {
    if (!setPageLayoutHeader || !clearPageLayoutHeader) return;

    const config: PageLayoutHeaderConfig = {
      title,
      secondaryTitle,
      actions,
    };

    setPageLayoutHeader(config);

    return () => {
      clearPageLayoutHeader();
    };
  }, [actions, clearPageLayoutHeader, secondaryTitle, setPageLayoutHeader, title]);

  const shouldRenderLocalHeader = !pageLayoutContext;
  const hasSecondaryRow = Boolean(secondaryTitle || actions);

  return (
    <Row justify="center">
      <div className="dashboard__content">
        {shouldRenderLocalHeader && (
          <div className="dashboard__content__header">
            <Title level={3} className="dashboard__content__title" style={{ marginBottom: 0 }}>
              {title}
            </Title>
            {hasSecondaryRow && (
              <div
                style={{
                  marginTop: 12,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 12,
                  alignItems: "center",
                }}
              >
                {secondaryTitle && (
                  <Paragraph type="secondary" style={{ margin: 0 }}>
                    {secondaryTitle}
                  </Paragraph>
                )}
                {actions && (
                  <div style={{ marginLeft: secondaryTitle ? "auto" : 0, flexShrink: 0 }}>{actions}</div>
                )}
              </div>
            )}
            <Divider />
          </div>
        )}
        {children}
      </div>
    </Row>
  );
};

export default DashboardViewContent;
